import { useEffect, useState } from 'react'
import { Link } from "react-router-dom";
import ROUTES from '../../routes';
import api from './../../config/axiosInstance';
import { showToast } from '../../utils/toast';
import { formatDate, handleErrors } from '../../utils/common';

function Projects() {
    const Swal = window.Swal;
    const [baseUrl, setBaseUrl] = useState([]);
    const limit = 5;
    const [projects, setProjects] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const controller = new AbortController();
        const fetchProjects = async (page = 1, searchQuery = "") => {
            try {
                setLoading(true);
                const offset = (page - 1) * limit;
                const res = await api.get(
                    `/projects/list?limit=${limit}&offset=${offset}&search=${encodeURIComponent(searchQuery)}`,
                    { signal: controller.signal }
                );
                if (res.status === 200) {
                    setProjects(res.data.projects);
                    setBaseUrl(res.data.base_url);
                    const total = res.data.total || 0;
                    setTotalPages(Math.ceil(total / limit));
                }
            } catch (err) {
                if (err.name !== "CanceledError") handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        const delayDebounce = setTimeout(() => {
            fetchProjects(currentPage, search);
        }, 500);

        return () => {
            clearTimeout(delayDebounce);
            controller.abort();
        };
    }, [currentPage, search]);

    const handleDelete = (slug) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.delete(`/projects/${slug}`);
                    if (res.status === 200) {
                        showToast('success', res.data.message, 'Success');
                        setProjects((prev) => prev.filter((a) => a.slug !== slug));
                    }
                } catch (err) {
                    handleErrors(err);
                }
            }
        });
    };

    const generatePagination = () => {
        const pages = [];
        const maxVisible = 5;
        const sidePages = 2;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > sidePages + 2) pages.push('...');
            const start = Math.max(2, currentPage - sidePages);
            const end = Math.min(totalPages - 1, currentPage + sidePages);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPages - sidePages - 1) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={ROUTES.PROJECTS}>Projects</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Projects</h4>
                        </div>
                        <div className="card-body">
                        
                            <div className="row">
                                <div className="col-12">
                                    <div className="row align-items-center">
                                        <div className="mb-3 col-lg-3 col-md-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="search"
                                                id="search"
                                                placeholder="Enter Your Keyword..."
                                                value={search}
                                                onChange={(e) => {
                                                    setCurrentPage(1);
                                                    setSearch(e.target.value);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center my-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="table table-responsive-md">
                                        <thead>
                                            <tr>
                                                <th className="width80"><strong>#</strong></th>
                                                <th><strong></strong></th>
                                                <th><strong>Title</strong></th>
                                                <th><strong>Created At</strong></th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {projects && projects.map((project, index) => (
                                                <tr key={project._id || index}>
                                                    <td><strong>{index + 1}</strong></td>
                                                    <td>
                                                        <img
                                                            className="me-2 img-fluid rounded-circle"
                                                            width={30}
                                                            src={
                                                                project.imageSrc?.startsWith('http')
                                                                    ? project.imageSrc
                                                                    : baseUrl + project.imageSrc
                                                            }
                                                            alt="/"
                                                        />
                                                    </td>
                                                    <td>{project?.title}</td>
                                                    <td>{formatDate(project?.t)}</td>
                                                    <td>
                                                        <div className="d-flex">
                                                            <Link to={`/project/${project.slug}/edit`} className="btn btn-primary shadow btn-xs sharp me-1">
                                                                <i className="fas fa-pencil-alt"></i>
                                                            </Link>
                                                            <a
                                                                href="#/"
                                                                className="btn btn-danger shadow btn-xs sharp"
                                                                onClick={() => handleDelete(project.slug)}
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="card-footer">
                            <ul className="pagination pagination-gutter pagination-primary no-bg">
                                <li className={`page-item page-indicator ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                        <i className="la la-angle-left"></i>
                                    </a>
                                </li>
                                {generatePagination().map((page, index) =>
                                    page === '...' ? (
                                        <li className="page-item disabled" key={index}>
                                            <span className="page-link">...</span>
                                        </li>
                                    ) : (
                                        <li className={`page-item ${currentPage === page ? 'active' : ''}`} key={index}>
                                            <a className="page-link" href="#/" onClick={() => setCurrentPage(page)}>{page}</a>
                                        </li>
                                    )
                                )}
                                <li className={`page-item page-indicator ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" href="#/" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                        <i className="la la-angle-right"></i>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Projects;
